import React, { useEffect, useState } from 'react';
import { G, styles, api } from '../theme.js';
import { Btn, Spinner, Input } from '../ui.jsx';

// Public estimate viewer with Good/Better/Best tier selection. URL: /estimate/:id?t=<token>
export function EstimateView() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [tier, setTier] = useState(null);
  const [signature, setSignature] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  // Supports both URL shapes:
  //   /estimate/:id         (legacy)
  //   /portal/estimate/:id  (customer-facing, sent in email/SMS)
  const segs = window.location.pathname.split('/').filter(Boolean);
  const id = segs[0] === 'portal' ? segs[2] : segs[1];
  // The customer's link includes ?t=<portal_token>. Forward it to the API so
  // the portal-token check passes. Without it the server returns 403 with
  // "Invalid or missing access token" and the page reads as broken.
  const tokenQS = (() => {
    try {
      const t = new URLSearchParams(window.location.search).get('t');
      return t ? `?t=${encodeURIComponent(t)}` : '';
    } catch { return ''; }
  })();

  useEffect(() => {
    if (!id) { setErr('Invalid link'); return; }
    api(`/api/portal/estimates/${id}${tokenQS}`).then(d => {
      setData(d);
      if (d.tiers?.length) setTier(d.tiers.find(t => t.key === 'better') || d.tiers[1] || d.tiers[0]);
    }).catch(e => setErr(e.message));
  }, [id]);

  const [membershipOffer, setMembershipOffer] = useState(null);
  const [questionOpen, setQuestionOpen] = useState(false);
  const [financingOpen, setFinancingOpen] = useState(false);
  // T&C must be explicitly accepted on the Sign step — it's the customer's
  // legal agreement with the business's Terms of Service for THIS work.
  const [tcAccepted, setTcAccepted] = useState(false);
  // Multi-step funnel: review → sign → (membership upsell) → pay → done
  const [flowStep, setFlowStep] = useState('review');
  const [postApproval, setPostApproval] = useState(null); // {payUrl, invoiceId, membershipOffer}
  // Approve click → move to the dedicated "sign" page (separate step, no
  // distractions). The actual decision POST happens after they sign.
  const startApproval = () => { setErr(null); setFlowStep('signing'); };
  const submit = async (decision) => {
    if (decision === 'approved' && !signature.trim()) return setErr('Please sign to approve.');
    if (decision === 'approved' && !tcAccepted) return setErr('Please accept the Terms of Service and Privacy Policy.');
    setBusy(true);
    try {
      // signature can be either a typed name OR a base64 PNG of a drawn
      // signature. The backend stores whichever form it receives.
      const isDrawn = signature.startsWith('data:image/');
      const r = await api(`/api/portal/estimates/${id}/decision`, {
        method: 'POST',
        body: {
          decision,
          signature: isDrawn ? '(drawn signature)' : signature.trim(),
          signatureImage: isDrawn ? signature : null,
          tcAccepted: tcAccepted,
          tcAcceptedAt: new Date().toISOString(),
          selectedTier: tier?.key || tier?.name,
        },
      });
      // Cache the invoice / pay URL so the Payment step can render it.
      setPostApproval({
        payUrl: r.payUrl || null,
        invoiceId: r.invoiceId || null,
        decision,
      });
      // If a membership offer came back, show that step first; otherwise
      // jump straight to the payment step (or done when declined).
      if (decision === 'approved' && r.membershipOffer) {
        setMembershipOffer(r.membershipOffer);
      } else if (decision === 'approved') {
        setFlowStep('payment');
      } else {
        setDone(decision);
      }
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const acceptMembership = async (billingCycle) => {
    setBusy(true);
    try {
      await api(`/api/portal/estimates/${id}/add-membership`, {
        method: 'POST',
        body: { planId: membershipOffer.planId, billingCycle },
      });
      setMembershipOffer({ ...membershipOffer, accepted: true });
      setTimeout(() => { setMembershipOffer(null); setFlowStep('payment'); }, 1800);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };
  const skipMembership = () => { setMembershipOffer(null); setFlowStep('payment'); };

  if (err) return <div style={page}><div style={{ ...styles.card, padding:30, textAlign:'center' }}><h2>Can't open estimate</h2><p style={{ color:G.textMuted }}>{err}</p></div></div>;
  if (!data) return <div style={page}><Spinner sz={36}/></div>;

  const isTiered = Array.isArray(data.tiers) && data.tiers.length > 0;
  const taxRate = data.taxRate || 7;
  const activeLines = isTiered ? (tier?.lines || []) : data.lines;
  const sub = activeLines.reduce((s, l) => s + (Number(l.qty)||1) * (Number(l.price)||0), 0);
  const tax = sub * taxRate / 100;
  const total = sub + tax;

  if (done) return <div style={page}><div style={{ ...styles.card, padding:30, textAlign:'center', maxWidth:480 }}>
    <h2 style={{ color: done === 'approved' ? G.green : G.text, margin:0 }}>{done === 'approved' ? '✓ Approved' : 'Got it'}</h2>
    <p style={{ color:G.textMuted, marginTop:8 }}>{done === 'approved' ? `${data.business?.name} will be in touch shortly to schedule.` : `Thanks for letting ${data.business?.name} know.`}</p>
  </div></div>;

  // Dedicated SIGN step — separate screen so the customer focuses on signing
  if (flowStep === 'signing') {
    return <div style={page}><div style={{ ...styles.card, padding:30, maxWidth:600, width:'100%' }}>
      <StepCrumbs current={1}/>
      <h2 style={{ margin:'10px 0 6px' }}>Sign to approve</h2>
      <p style={{ color:G.textMuted, fontSize:14, marginBottom:20 }}>
        You're approving the <strong>{tier?.name || 'estimate'}</strong> from {data.business?.name} for <strong>${total.toFixed(2)}</strong>.
      </p>
      {data.paymentPlan && data.paymentPlan.type !== 'in_full' && <PaymentPlanBanner plan={data.paymentPlan} total={total}/>}
      <div style={{ marginTop:18 }}>
        <SignaturePad value={signature} onChange={setSignature} businessName={data.business?.name}/>
        <div style={{ fontSize:11, color:G.textMuted, marginTop:8 }}>
          By signing, you authorize {data.business?.name} to perform the work described above for ${total.toFixed(2)}. This signature has the same legal effect as a handwritten signature.
        </div>
      </div>
      <div style={{ marginTop:16, padding:'12px 14px', background:G.input, borderRadius:8, border:`1px solid ${G.cardBorder}` }}>
        <label style={{ display:'flex', gap:10, alignItems:'flex-start', cursor:'pointer', fontSize:13, lineHeight:1.5 }}>
          <input type="checkbox" checked={tcAccepted} onChange={e=>setTcAccepted(e.target.checked)} style={{ marginTop:3, width:18, height:18, flexShrink:0, cursor:'pointer' }}/>
          <span>
            <strong>Required:</strong> I have read and accept the
            <a href={data.business?.customer_terms_mode === 'upload' && data.business?.customer_terms_url ? data.business.customer_terms_url : `/lp/a/${data.business?.lp_slug || ''}/terms`} target="_blank" rel="noreferrer" style={{ color:G.cyan, marginLeft:4 }}>Terms of Service</a> and
            <a href={`/lp/a/${data.business?.lp_slug || ''}/privacy`} target="_blank" rel="noreferrer" style={{ color:G.cyan, marginLeft:4 }}>Privacy Policy</a>
            {' '}of {data.business?.name}, including the workmanship warranty, payment terms, and scope of work for this estimate.
          </span>
        </label>
      </div>
      {err && <div style={{ marginTop:14, padding:'10px 14px', borderRadius:8, background:G.red+'22', color:G.red, fontSize:13 }}>{err}</div>}
      <div style={{ display:'flex', gap:10, marginTop:20 }}>
        <Btn v="ghost" sz="lg" onClick={()=>setFlowStep('review')} disabled={busy} style={{ flex:1 }}>← Back</Btn>
        <Btn v="primary" sz="lg" onClick={()=>submit('approved')} disabled={busy || !signature.trim() || !tcAccepted} style={{ flex:2 }}>
          {busy ? <Spinner/> : `Sign & Approve · $${total.toFixed(2)}`}
        </Btn>
      </div>
    </div>
    {membershipOffer && <MembershipOrderBump
      offer={membershipOffer} busy={busy}
      onAccept={acceptMembership} onSkip={skipMembership}
      businessName={data.business?.name}
    />}
    </div>;
  }

  // Dedicated PAYMENT step — shown after sign + membership choice. Auto-pushes
  // them to the Stripe pay page if we have one; otherwise shows the invoice
  // summary with a manual "Pay now" button + "Pay later" escape.
  if (flowStep === 'payment') {
    return <div style={page}><div style={{ ...styles.card, padding:30, maxWidth:560, width:'100%', textAlign:'center' }}>
      <StepCrumbs current={2}/>
      <div style={{ fontSize:54 }}>✅</div>
      <h2 style={{ margin:'8px 0', color:G.green }}>Estimate Approved</h2>
      <p style={{ color:G.textMuted, fontSize:14, marginBottom:18 }}>
        {data.business?.name} just sent your invoice. You can pay now or wait — the link is in your inbox + texts too.
      </p>
      <div style={{ padding:18, borderRadius:12, background:G.input, marginBottom:18 }}>
        <div style={{ fontSize:11, color:G.textMuted, fontWeight:700, textTransform:'uppercase', letterSpacing:'.06em' }}>Amount due</div>
        <div style={{ fontSize:36, fontWeight:800, color:G.green, marginTop:4 }}>${total.toFixed(2)}</div>
        {data.paymentPlan?.type === 'deposit_balance' && data.paymentPlan.deposit && <div style={{ fontSize:12, color:G.textMuted, marginTop:6 }}>
          Today's deposit only — balance billed on completion.
        </div>}
      </div>
      <div style={{ display:'grid', gap:10 }}>
        {postApproval?.payUrl ? (
          <Btn v="primary" sz="lg" onClick={()=>{ window.location.href = postApproval.payUrl; }} style={{ background:G.green, borderColor:G.green }}>
            💳 Pay Now — Apple Pay, Google Pay, Card or Bank
          </Btn>
        ) : (
          <div style={{ fontSize:13, color:G.textMuted, padding:'14px', background:G.input, borderRadius:8 }}>
            Online payment isn't set up yet for this business — they'll send a pay link to your phone shortly.
          </div>
        )}
        <button onClick={()=>setDone('approved')} style={{
          width:'100%', padding:'12px', background:'transparent', border:'none',
          color:G.textMuted, fontSize:13, cursor:'pointer',
        }}>Pay later — I'll use the link they texted me</button>
      </div>
    </div></div>;
  }

  return <div style={page}>
    <div style={{ ...styles.card, padding:30, maxWidth: isTiered ? 1200 : 820, width:'100%' }}>
      <div style={{ fontSize:11, fontWeight:700, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.08em' }}>{data.business?.name}</div>
      <h2 style={{ margin:'4px 0' }}>Estimate {data.num} — {data.title}</h2>
      <div style={{ color:G.textMuted, fontSize:13 }}>For {data.customer?.firstName} {data.customer?.lastName} · Valid until {data.validUntil}</div>

      {isTiered ? <>
        <div style={{ marginTop:22, fontSize:14, color:G.text, fontWeight:600 }}>Compare your three options — pick the one that fits.</div>
        <div style={{
          marginTop:14,
          display:'grid',
          gridTemplateColumns:'repeat(3, minmax(0, 1fr))',
          gap:14,
        }} className="gbb-grid">
          {data.tiers.map((t, idx) => {
            const active = tier?.key === t.key || tier?.name === t.name;
            const tSub = (t.lines||[]).reduce((s, l) => s + (Number(l.qty)||1) * (Number(l.price)||0), 0);
            const tTax = tSub * taxRate / 100;
            const tTotal = tSub + tTax;
            const accent = idx === 0 ? G.cyan : idx === 1 ? G.purple : G.green;
            return <div key={t.key||t.name} onClick={()=>setTier(t)} style={{
              padding:18, borderRadius:14, cursor:'pointer',
              background: active ? `linear-gradient(180deg, ${accent}22, ${G.input})` : G.input,
              border: `2px solid ${active ? accent : G.cardBorder}`,
              position:'relative',
              display:'flex', flexDirection:'column',
              transition:'transform .12s ease, border-color .12s ease',
              transform: active ? 'translateY(-4px)' : 'none',
              boxShadow: active ? `0 8px 24px ${accent}33` : 'none',
            }}>
              {t.key === 'better' && <div style={{ position:'absolute', top:-11, left:'50%', transform:'translateX(-50%)', background:G.purple, color:'#fff', fontSize:11, padding:'3px 12px', borderRadius:99, fontWeight:800, letterSpacing:'.04em' }}>MOST POPULAR</div>}
              <div style={{ fontSize:11, fontWeight:800, color:accent, textTransform:'uppercase', letterSpacing:'.08em' }}>{idx === 0 ? 'Good' : idx === 1 ? 'Better' : 'Best'}</div>
              <div style={{ fontWeight:800, fontSize:19, marginTop:4 }}>{t.name}</div>
              <div style={{ fontSize:12, color:G.textMuted, marginTop:2 }}>{t.label}</div>
              {t.blurb && <div style={{ fontSize:12, color:G.textMuted, marginTop:8, lineHeight:1.45 }}>{t.blurb}</div>}

              <div style={{ marginTop:14, padding:'12px 0', borderTop:`1px solid ${G.border}`, borderBottom:`1px solid ${G.border}`, flex:1 }}>
                <div style={{ fontSize:10, fontWeight:700, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.06em', marginBottom:8 }}>What's included</div>
                {(t.lines||[]).map((l, i) => (
                  <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr auto', gap:6, padding:'4px 0', fontSize:12, lineHeight:1.35 }}>
                    <div>
                      <span style={{ color:accent, marginRight:6, fontWeight:700 }}>✓</span>
                      {l.name || l.description}
                      {Number(l.qty) > 1 && <span style={{ color:G.textMuted }}> ×{l.qty}</span>}
                    </div>
                    <div style={{ color:G.textMuted, fontVariantNumeric:'tabular-nums' }}>${((Number(l.qty)||1) * (Number(l.price)||0)).toFixed(0)}</div>
                  </div>
                ))}
                {(!t.lines || t.lines.length === 0) && <div style={{ color:G.textMuted, fontSize:12, fontStyle:'italic' }}>No items in this tier</div>}
              </div>

              <div style={{ marginTop:12, fontSize:12 }}>
                <div style={{ display:'flex', justifyContent:'space-between', color:G.textMuted, padding:'2px 0' }}><span>Subtotal</span><span>${tSub.toFixed(2)}</span></div>
                <div style={{ display:'flex', justifyContent:'space-between', color:G.textMuted, padding:'2px 0' }}><span>Tax ({taxRate}%)</span><span>${tTax.toFixed(2)}</span></div>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginTop:6, paddingTop:8, borderTop:`1px dashed ${G.border}` }}>
                  <span style={{ fontWeight:700 }}>Total</span>
                  <span style={{ fontWeight:800, fontSize:24, color:accent }}>${tTotal.toFixed(0)}</span>
                </div>
              </div>

              <Btn
                v={active ? 'primary' : 'ghost'}
                onClick={(e)=>{ e.stopPropagation(); setTier(t); }}
                style={{ marginTop:14, width:'100%', background: active ? accent : undefined, borderColor: active ? accent : undefined }}
              >
                {active ? '✓ Selected' : `Choose ${idx === 0 ? 'Good' : idx === 1 ? 'Better' : 'Best'}`}
              </Btn>
            </div>;
          })}
        </div>
        <style>{`
          @media (max-width: 860px) {
            .gbb-grid { grid-template-columns: 1fr !important; }
          }
        `}</style>
      </> : <>
        <div style={{ marginTop:22, padding:'14px 0', borderTop:`1px solid ${G.border}`, borderBottom:`1px solid ${G.border}` }}>
          {activeLines.map((l, i) => <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr 60px 90px 90px', gap:10, padding:'5px 0', fontSize:13 }}>
            <div>{l.name || l.description}</div>
            <div style={{ textAlign:'right', color:G.textMuted }}>×{l.qty || 1}</div>
            <div style={{ textAlign:'right', color:G.textMuted }}>${Number(l.price||0).toFixed(2)}</div>
            <div style={{ textAlign:'right', fontWeight:600 }}>${((Number(l.qty)||1) * (Number(l.price)||0)).toFixed(2)}</div>
          </div>)}
        </div>

        <div style={{ marginTop:14, fontSize:14 }}>
          <Row k="Subtotal" v={`$${sub.toFixed(2)}`}/>
          <Row k={`Tax (${taxRate}%)`} v={`$${tax.toFixed(2)}`}/>
          <Row k={<strong>Total</strong>} v={<strong style={{ fontSize:18 }}>${total.toFixed(2)}</strong>}/>
        </div>
      </>}

      {data.paymentPlan && data.paymentPlan.type !== 'in_full' && <PaymentPlanBanner plan={data.paymentPlan} total={total}/>}

      {/* 3-button CTA row: Have Questions · Approve · Finance from $0 down.
          No APR teasers — the finance flow does a soft-pull questionnaire
          to push the highest-approval-likelihood partner. Approve takes the
          customer to the dedicated Sign page (separate step). */}
      <div style={{ marginTop:22 }}>
        <StepCrumbs current={0}/>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1.4fr 1.2fr', gap:10, marginTop:14 }} className="cta-row">
        <Btn v="ghost" sz="lg" disabled={busy} onClick={()=>setQuestionOpen(true)}>Have Questions</Btn>
        <Btn v="primary" sz="lg" disabled={busy} onClick={startApproval}>
          {busy ? <Spinner/> : `Approve · $${total.toFixed(2)}`}
        </Btn>
        <Btn sz="lg" disabled={busy} onClick={()=>setFinancingOpen(true)} style={{ background:G.green, borderColor:G.green, color:'#fff' }}>
          💳 Finance · $0 down
        </Btn>
      </div>
      <div style={{ marginTop:8, fontSize:11, color:G.textMuted, textAlign:'center' }}>
        Financing is a 30-second soft pull — no impact to your credit.
      </div>
      <style>{`
        @media (max-width: 640px) { .cta-row { grid-template-columns: 1fr !important; } }
      `}</style>

      <div style={{ marginTop:14 }}>
        <button onClick={()=>submit('declined')} disabled={busy} style={{
          width:'100%', padding:'12px', background:'transparent', border:'none',
          color:G.textMuted, fontSize:13, cursor:'pointer',
        }}>Not interested — decline</button>
      </div>

      <div style={{ marginTop:24, fontSize:11, color:G.textDim, textAlign:'center' }}>
        Powered by TradeOps
      </div>
    </div>
    {membershipOffer && <MembershipOrderBump
      offer={membershipOffer}
      busy={busy}
      onAccept={acceptMembership}
      onSkip={skipMembership}
      businessName={data.business?.name}
    />}
    {questionOpen && <HaveQuestionModal estimateId={id} businessName={data.business?.name} onClose={()=>setQuestionOpen(false)}/>}
    {financingOpen && <FinancingQuestionnaireModal estimateId={id} tokenQS={tokenQS} total={total} onClose={()=>setFinancingOpen(false)}/>}
  </div>;
}

// Lightweight "Have Questions" modal — texts the office instead of jumping
// the customer through to a contact form.
function HaveQuestionModal({ estimateId, businessName, onClose }) {
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const send = async () => {
    if (!msg.trim()) return;
    setBusy(true);
    try {
      await api(`/api/portal/estimates/${estimateId}/question`, { method:'POST', body:{ question: msg }});
      setSent(true);
    } catch (e) { alert(e.message); }
    finally { setBusy(false); }
  };
  return <div style={overlay} onClick={onClose}><div style={modalCard} onClick={e=>e.stopPropagation()}>
    {sent ? <>
      <div style={{ fontSize:48, textAlign:'center' }}>📩</div>
      <h2 style={{ textAlign:'center', margin:'10px 0' }}>We got your question</h2>
      <p style={{ textAlign:'center', color:G.textMuted, marginBottom:18 }}>{businessName} will text or call you back shortly.</p>
      <Btn v="primary" full onClick={onClose}>Close</Btn>
    </> : <>
      <h2 style={{ margin:'0 0 6px' }}>Have Questions?</h2>
      <p style={{ color:G.textMuted, fontSize:13, marginBottom:14 }}>Send {businessName} a quick note — they'll text you back right away.</p>
      <textarea
        value={msg} onChange={e=>setMsg(e.target.value)}
        placeholder="What would you like to know about this estimate?"
        rows={5}
        style={{ width:'100%', padding:12, border:`1px solid ${G.cardBorder}`, borderRadius:8, fontSize:14, fontFamily:'inherit', resize:'vertical' }}
      />
      <div style={{ display:'flex', gap:8, marginTop:14 }}>
        <Btn v="ghost" onClick={onClose} disabled={busy} style={{ flex:1 }}>Cancel</Btn>
        <Btn v="primary" onClick={send} disabled={busy || !msg.trim()} style={{ flex:1 }}>{busy?<Spinner/>:'Send Question'}</Btn>
      </div>
    </>}
  </div></div>;
}

// Soft-pull financing questionnaire — 3 short questions, then we recommend
// the partner most likely to approve and push the customer there. No APRs
// shown in this flow — just outcomes ($0 down + monthly payment range).
function FinancingQuestionnaireModal({ estimateId, tokenQS, total, onClose }) {
  const [step, setStep] = useState(0);
  const [creditProfile, setCreditProfile] = useState(null);
  const [monthlyBudget, setMonthlyBudget] = useState(null);
  const [termPreference, setTermPreference] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api(`/api/portal/estimates/${estimateId}/financing-match${tokenQS}`, {
        method:'POST', body:{ creditProfile, monthlyBudget, termPreference },
      });
      setResult(r);
    } catch (e) { alert(e.message); onClose(); }
    finally { setBusy(false); }
  };
  const Choice = ({ value, label, sub, onPick, selected }) => (
    <button onClick={onPick} style={{
      padding:'14px 16px', borderRadius:10, fontSize:14, fontWeight:600, cursor:'pointer',
      background: selected ? G.green : G.input,
      color: selected ? '#fff' : G.text,
      border: `1px solid ${selected ? G.green : G.cardBorder}`,
      textAlign:'left', width:'100%', marginBottom:8,
    }}>
      <div>{label}</div>
      {sub && <div style={{ fontSize:11, opacity:.85, fontWeight:500, marginTop:2 }}>{sub}</div>}
    </button>
  );

  // Result step: show the recommended partner + Continue → opens partner site.
  if (result?.ok) {
    return <div style={overlay} onClick={onClose}><div style={modalCard} onClick={e=>e.stopPropagation()}>
      <div style={{ textAlign:'center', fontSize:48 }}>🎯</div>
      <h2 style={{ textAlign:'center', margin:'8px 0' }}>You're a strong match for {result.partner.label}</h2>
      <p style={{ color:G.textMuted, fontSize:13, textAlign:'center', marginBottom:18 }}>
        Based on your answers, this lender has the highest chance of approving you.
        {result.partner.downPayment === 0 && <> <strong style={{ color:G.green }}>$0 down to start.</strong></>}
      </p>
      <div style={{ padding:16, borderRadius:10, background:G.greenLight, marginBottom:18 }}>
        <div style={{ fontSize:11, color:G.green, fontWeight:800, textTransform:'uppercase', letterSpacing:'.05em' }}>What happens next</div>
        <ol style={{ marginTop:8, fontSize:13, color:G.text, paddingLeft:20, lineHeight:1.6 }}>
          <li>Tap Continue → {result.partner.label}'s short application</li>
          <li>Enter your info (takes ~30 seconds)</li>
          <li>Soft-pull credit check — <strong>no impact</strong> to your score</li>
          <li>See your offer instantly — accept or shop other terms</li>
        </ol>
      </div>
      <div style={{ display:'flex', gap:8 }}>
        <Btn v="ghost" onClick={onClose} style={{ flex:1 }}>Back</Btn>
        <Btn v="primary" onClick={()=>{ window.open(result.partner.applyUrl, '_blank'); onClose(); }} style={{ flex:2, background:G.green, borderColor:G.green }}>
          Continue → {result.partner.label}
        </Btn>
      </div>
    </div></div>;
  }
  if (result && !result.ok) {
    return <div style={overlay} onClick={onClose}><div style={modalCard} onClick={e=>e.stopPropagation()}>
      <div style={{ textAlign:'center', fontSize:36 }}>📋</div>
      <h2 style={{ textAlign:'center', margin:'8px 0' }}>Let's try a different path</h2>
      <p style={{ color:G.textMuted, fontSize:13, textAlign:'center', marginBottom:18 }}>{result.message}</p>
      <Btn v="primary" full onClick={onClose}>Back to estimate</Btn>
    </div></div>;
  }
  return <div style={overlay} onClick={onClose}>
    <div style={modalCard} onClick={e=>e.stopPropagation()}>
      <div style={{ fontSize:11, fontWeight:800, color:G.green, textTransform:'uppercase', letterSpacing:'.06em' }}>Step {step+1} of 3</div>
      <h2 style={{ margin:'4px 0 6px' }}>
        {step === 0 && 'What does your credit look like?'}
        {step === 1 && "What's a comfortable monthly payment?"}
        {step === 2 && 'How long do you want to spread payments?'}
      </h2>
      <p style={{ color:G.textMuted, fontSize:13, marginBottom:18 }}>
        Honest answers help us pick the lender most likely to approve you. <strong>No credit pull yet.</strong>
      </p>

      {step === 0 && <div>
        <Choice value="excellent" label="Excellent" sub="720+ — I pay on time and have a long history"
          selected={creditProfile==='excellent'} onPick={()=>setCreditProfile('excellent')}/>
        <Choice value="good" label="Good" sub="660 – 719 — Mostly on-time, few hiccups"
          selected={creditProfile==='good'} onPick={()=>setCreditProfile('good')}/>
        <Choice value="fair" label="Fair" sub="580 – 659 — Some past issues but recovering"
          selected={creditProfile==='fair'} onPick={()=>setCreditProfile('fair')}/>
        <Choice value="building" label="Building / Rebuilding" sub="Below 580 or no credit history"
          selected={creditProfile==='building'} onPick={()=>setCreditProfile('building')}/>
      </div>}
      {step === 1 && <div>
        <Choice value="low" label="Under $100/mo" selected={monthlyBudget==='low'} onPick={()=>setMonthlyBudget('low')}/>
        <Choice value="mid" label="$100 – $250/mo" selected={monthlyBudget==='mid'} onPick={()=>setMonthlyBudget('mid')}/>
        <Choice value="high" label="$250 – $500/mo" selected={monthlyBudget==='high'} onPick={()=>setMonthlyBudget('high')}/>
        <Choice value="any" label="Whatever it takes to spread it out" selected={monthlyBudget==='any'} onPick={()=>setMonthlyBudget('any')}/>
      </div>}
      {step === 2 && <div>
        <Choice value="short" label="1 – 2 years" sub="Pay it off fast" selected={termPreference==='short'} onPick={()=>setTermPreference('short')}/>
        <Choice value="medium" label="3 – 5 years" sub="Balanced — most popular" selected={termPreference==='medium'} onPick={()=>setTermPreference('medium')}/>
        <Choice value="long" label="5 – 10 years" sub="Lowest monthly payment" selected={termPreference==='long'} onPick={()=>setTermPreference('long')}/>
      </div>}

      <div style={{ display:'flex', gap:8, marginTop:14 }}>
        {step > 0 && <Btn v="ghost" onClick={()=>setStep(step-1)} disabled={busy} style={{ flex:1 }}>Back</Btn>}
        {step < 2 && <Btn v="primary" onClick={()=>setStep(step+1)}
          disabled={ (step===0 && !creditProfile) || (step===1 && !monthlyBudget) }
          style={{ flex:2, background:G.green, borderColor:G.green }}>Next →</Btn>}
        {step === 2 && <Btn v="primary" onClick={submit} disabled={busy || !termPreference} style={{ flex:2, background:G.green, borderColor:G.green }}>
          {busy ? <Spinner/> : 'Find my best match →'}
        </Btn>}
      </div>
      <button onClick={onClose} disabled={busy} style={{
        marginTop:10, width:'100%', padding:'10px', background:'transparent', border:'none',
        color:G.textMuted, fontSize:13, cursor:'pointer',
      }}>Cancel</button>
    </div>
  </div>;
}

// Post-approval order-bump. Slides up as a modal the moment the customer
// signs. Shows what they paid today vs. what a member would have paid, plus
// projected 12-month savings.
function MembershipOrderBump({ offer, busy, onAccept, onSkip, businessName }) {
  const s = offer.savings || {};
  if (offer.accepted) {
    return <div style={overlay}><div style={{ ...modalCard, textAlign:'center' }}>
      <div style={{ fontSize:48 }}>🎉</div>
      <h2 style={{ margin:'8px 0', color:G.green }}>You're a member!</h2>
      <p style={{ color:G.textMuted, marginTop:6 }}>{businessName} will activate your benefits right away.</p>
    </div></div>;
  }
  return <div style={overlay}>
    <div style={modalCard}>
      <div style={{ fontSize:11, fontWeight:800, color:G.purple, textTransform:'uppercase', letterSpacing:'.08em' }}>One last thing</div>
      <h2 style={{ margin:'4px 0 6px', fontSize:22 }}>Want to join the {offer.name}?</h2>
      <p style={{ color:G.textMuted, fontSize:13, marginTop:0 }}>{offer.description || `Members of ${businessName} save on every visit and skip the line.`}</p>

      {/* Today-only urgency banner */}
      {offer.todayOnly?.firstMonthFree && <div style={{ marginTop:12, padding:'10px 14px', borderRadius:8, background:`linear-gradient(135deg, ${G.yellow}33, ${G.yellow}11)`, border:`1px solid ${G.yellow}66`, fontSize:13, color:G.text }}>
        🎁 <strong style={{ color:G.yellow }}>Today only:</strong> {offer.todayOnly.urgencyCopy || 'Sign up before you leave this page and get your first month free.'}
      </div>}

      {/* Side-by-side: savings vs. cost */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginTop:14 }} className="member-grid">
        <div style={{ padding:14, borderRadius:10, background:G.greenLight, border:`1px solid ${G.green}66` }}>
          <div style={{ fontSize:11, fontWeight:800, color:G.green, textTransform:'uppercase', letterSpacing:'.06em' }}>You'd save</div>
          <div style={{ marginTop:8 }}>
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, padding:'4px 0' }}>
              <span style={{ color:G.textMuted }}>On today's job</span>
              <strong style={{ color:G.green }}>${Number(s.savedOnThisJob||0).toFixed(0)}</strong>
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, padding:'4px 0' }}>
              <span style={{ color:G.textMuted }}>Over the next 12 months</span>
              <strong style={{ color:G.green }}>${Number(s.projectedAnnualSavings||0).toFixed(0)}</strong>
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, padding:'8px 0 0', marginTop:8, borderTop:`1px dashed ${G.green}55` }}>
              <span style={{ fontWeight:700 }}>Net benefit</span>
              <strong style={{ color:G.green, fontSize:18 }}>${Number(s.netAnnualBenefit||0).toFixed(0)}</strong>
            </div>
          </div>
        </div>
        <div style={{ padding:14, borderRadius:10, background:G.input, border:`1px solid ${G.cardBorder}` }}>
          <div style={{ fontSize:11, fontWeight:800, color:G.text, textTransform:'uppercase', letterSpacing:'.06em' }}>It costs</div>
          {offer.todayOnly?.firstMonthFree ? <>
            <div style={{ fontSize:32, fontWeight:800, marginTop:8, color:G.green }}>$0<span style={{ fontSize:14, color:G.textMuted, fontWeight:500 }}> first month</span></div>
            <div style={{ fontSize:12, color:G.textMuted, marginTop:2 }}>then ${Number(offer.monthlyPrice||0).toFixed(0)}/mo · cancel anytime</div>
          </> : <>
            <div style={{ fontSize:32, fontWeight:800, marginTop:8 }}>${Number(offer.monthlyPrice||0).toFixed(0)}<span style={{ fontSize:14, color:G.textMuted, fontWeight:500 }}>/mo</span></div>
            {offer.annualPrice > 0 && <div style={{ fontSize:12, color:G.textMuted, marginTop:4 }}>or ${offer.annualPrice}/year (save ${(offer.monthlyPrice*12 - offer.annualPrice).toFixed(0)})</div>}
          </>}
          <div style={{ fontSize:12, color:G.textMuted, marginTop:10 }}>{offer.memberDiscountPct}% off every service, anytime.</div>
        </div>
      </div>

      {offer.benefits?.length > 0 && <div style={{ marginTop:14, padding:14, borderRadius:10, background:G.input }}>
        <div style={{ fontSize:11, fontWeight:800, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.06em', marginBottom:8 }}>What you get</div>
        {offer.benefits.map((b, i) => (
          <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:8, padding:'4px 0', fontSize:13 }}>
            <span style={{ color:G.green, fontWeight:700 }}>✓</span>
            <span>{b}</span>
          </div>
        ))}
        {offer.visitsPerYear > 0 && <div style={{ display:'flex', alignItems:'flex-start', gap:8, padding:'4px 0', fontSize:13 }}>
          <span style={{ color:G.green, fontWeight:700 }}>✓</span>
          <span>{offer.visitsPerYear} preventive maintenance visit{offer.visitsPerYear===1?'':'s'} per year</span>
        </div>}
        <div style={{ display:'flex', alignItems:'flex-start', gap:8, padding:'4px 0', fontSize:13 }}>
          <span style={{ color:G.green, fontWeight:700 }}>✓</span>
          <span>Priority scheduling — members go first</span>
        </div>
      </div>}

      <div style={{ display:'grid', gridTemplateColumns: offer.annualPrice > 0 ? '1fr 1fr' : '1fr', gap:8, marginTop:18 }}>
        <Btn v="primary" sz="lg" disabled={busy} onClick={()=>onAccept('monthly')} style={{ background:G.green, borderColor:G.green }}>
          {busy ? <Spinner/> : offer.todayOnly?.firstMonthFree ? `Yes — start free this month` : `Join — $${Number(offer.monthlyPrice||0).toFixed(0)}/mo`}
        </Btn>
        {offer.annualPrice > 0 && <Btn v="primary" sz="lg" disabled={busy} onClick={()=>onAccept('annual')}>
          {offer.todayOnly?.firstMonthFree ? `Pay annual — $${Number(offer.todayOnly.todayAnnual||offer.annualPrice).toFixed(0)} (1 mo free)` : `Pay annual — $${Number(offer.annualPrice||0).toFixed(0)}`}
        </Btn>}
      </div>
      <button onClick={onSkip} disabled={busy} style={{
        marginTop:10, width:'100%', padding:'10px', background:'transparent', border:'none',
        color:G.textMuted, fontSize:13, cursor:'pointer',
      }}>No thanks, continue without membership</button>
      <style>{`
        @media (max-width: 540px) { .member-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </div>
  </div>;
}

const overlay = { position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', backdropFilter:'blur(6px)', display:'flex', alignItems:'center', justifyContent:'center', padding:18, zIndex:9999 };
const modalCard = { ...styles.card, padding:24, maxWidth:560, width:'100%', maxHeight:'90vh', overflow:'auto', boxShadow:'0 20px 60px rgba(0,0,0,.5)' };

function Row({ k, v }) {
  return <div style={{ display:'flex', justifyContent:'space-between', padding:'3px 0' }}>
    <span style={{ color:G.textMuted }}>{k}</span><span>{v}</span>
  </div>;
}

// Signature pad — customers can either TYPE their name (italic cursive
// rendering) OR DRAW their signature with a finger/mouse on canvas. The
// drawn signature is captured as a base64 PNG and sent up alongside the
// typed text. Either path satisfies "signed" — both paths satisfy E-SIGN
// + UETA for service agreements.
function SignaturePad({ value, onChange, businessName }) {
  const [mode, setMode] = React.useState('type'); // 'type' | 'draw'
  const canvasRef = React.useRef(null);
  const [drawing, setDrawing] = React.useState(false);
  const [hasInk, setHasInk] = React.useState(false);

  const getCtx = () => canvasRef.current?.getContext('2d');
  const point = (e) => {
    const c = canvasRef.current; if (!c) return null;
    const r = c.getBoundingClientRect();
    const t = e.touches?.[0] || e.changedTouches?.[0];
    const cx = t ? t.clientX : e.clientX;
    const cy = t ? t.clientY : e.clientY;
    return { x: (cx - r.left) * (c.width / r.width), y: (cy - r.top) * (c.height / r.height) };
  };
  const start = (e) => { e.preventDefault?.(); const p = point(e); if (!p) return; setDrawing(true); const ctx = getCtx(); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const move = (e) => {
    if (!drawing) return; e.preventDefault?.();
    const p = point(e); if (!p) return;
    const ctx = getCtx();
    ctx.lineWidth = 2.5; ctx.lineCap = 'round'; ctx.strokeStyle = '#0e0f12';
    ctx.lineTo(p.x, p.y); ctx.stroke();
    setHasInk(true);
  };
  const end = (e) => { e?.preventDefault?.(); if (!drawing) return; setDrawing(false); commit(); };
  const commit = () => {
    const c = canvasRef.current; if (!c) return;
    onChange(c.toDataURL('image/png'));
  };
  const clear = () => {
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext('2d');
    ctx.clearRect(0, 0, c.width, c.height);
    setHasInk(false);
    onChange('');
  };
  // White background under the strokes — required so PNG isn't transparent
  // when embedded into the signed agreement document later.
  React.useEffect(() => {
    if (mode !== 'draw') return;
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, c.width, c.height);
  }, [mode]);

  return <div>
    <div style={{ display:'flex', gap:6, marginBottom:8 }}>
      <button onClick={()=>{ setMode('type'); onChange(''); }} style={{
        padding:'6px 12px', borderRadius:6, fontSize:12, fontWeight:700, cursor:'pointer',
        border:`1px solid ${mode==='type'?G.purple:G.cardBorder}`,
        background: mode==='type' ? G.purpleDim : 'transparent',
        color: mode==='type' ? G.purple : G.textMuted,
      }}>⌨ Type signature</button>
      <button onClick={()=>{ setMode('draw'); onChange(''); }} style={{
        padding:'6px 12px', borderRadius:6, fontSize:12, fontWeight:700, cursor:'pointer',
        border:`1px solid ${mode==='draw'?G.purple:G.cardBorder}`,
        background: mode==='draw' ? G.purpleDim : 'transparent',
        color: mode==='draw' ? G.purple : G.textMuted,
      }}>✍ Draw signature</button>
    </div>
    {mode === 'type' ? <>
      <div style={{ fontSize:12, color:G.textMuted, marginBottom:6, fontWeight:600 }}>Type your full legal name:</div>
      <Input value={value || ''} onChange={e=>onChange(e.target.value)} placeholder="Your full name" autoFocus
        style={{ fontSize:22, padding:'14px 16px', fontFamily:'Brush Script MT, cursive', fontStyle:'italic' }}/>
    </> : <>
      <div style={{ fontSize:12, color:G.textMuted, marginBottom:6, fontWeight:600 }}>Draw your signature with your finger or mouse:</div>
      <div style={{ position:'relative', border:`1px solid ${G.cardBorder}`, borderRadius:8, background:'#fff', overflow:'hidden' }}>
        <canvas
          ref={canvasRef} width={520} height={180}
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          style={{ display:'block', width:'100%', height:180, touchAction:'none', cursor:'crosshair' }}
        />
        {!hasInk && <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', pointerEvents:'none', color:'#9ca3af', fontSize:14, fontStyle:'italic' }}>
          Tap and drag to sign
        </div>}
      </div>
      <button onClick={clear} style={{ marginTop:6, background:'transparent', border:'none', color:G.textMuted, fontSize:12, cursor:'pointer', textDecoration:'underline' }}>Clear &amp; redraw</button>
    </>}
  </div>;
}

// Step crumbs across the approval funnel: Review → Sign → Pay → Done.
// Highlights the current step in green so the customer sees how far they
// are. Hidden on the final done screen which has its own confirmation.
function StepCrumbs({ current }) {
  const steps = ['Review', 'Sign', 'Pay'];
  return <div style={{ display:'flex', gap:6, alignItems:'center', marginBottom:8, fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'.06em' }}>
    {steps.map((s, i) => (
      <React.Fragment key={s}>
        <span style={{
          padding:'4px 10px', borderRadius:99,
          background: i === current ? G.green : i < current ? G.greenLight : G.input,
          color: i === current ? '#fff' : i < current ? G.green : G.textMuted,
        }}>{i < current ? '✓ ' : ''}{s}</span>
        {i < steps.length - 1 && <span style={{ color:G.textMuted, fontSize:10 }}>›</span>}
      </React.Fragment>
    ))}
  </div>;
}

function FinancingPanel({ estimateId, total }) {
  const [data, setData] = useState(null);
  const [applying, setApplying] = useState(false);
  useEffect(() => {
    api(`/api/portal/estimates/${estimateId}/financing-quote`).then(setData).catch(()=>{});
  }, [estimateId, total]);

  if (!data || !data.partners?.length) return null;
  const rec = data.partners.find(p => p.isRecommended) || data.partners[0];
  const bestPayment = rec.payments?.find(p => p.termMonths === 60) || rec.payments?.[rec.payments.length-1];

  const apply = async () => {
    setApplying(true);
    try {
      const r = await api(`/api/portal/estimates/${estimateId}/finance`, { method:'POST' });
      if (r.partnerUrl) window.open(r.partnerUrl, '_blank');
    } catch (e) { alert(e.message); }
    finally { setApplying(false); }
  };

  return <div style={{ marginTop:22, padding:18, borderRadius:12, border:`2px solid ${G.green}66`, background: `linear-gradient(135deg, ${G.greenLight}, transparent)` }}>
    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
      <h3 style={{ margin:0, fontSize:17 }}>Financing available</h3>
      <span style={{ marginLeft:'auto', fontSize:12, color:G.textMuted }}>30-second approval, no impact to your credit</span>
    </div>
    {bestPayment && <div style={{ fontSize:14, color:G.text, marginBottom:14 }}>
      As low as <strong style={{ fontSize:24, color:G.green }}>${bestPayment.monthlyPaymentAllIn.toFixed(0)}/mo</strong>
      <span style={{ color:G.textMuted, fontSize:13 }}> for {bestPayment.termMonths} months</span>
    </div>}

    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:10, marginBottom:14 }}>
      {data.partners.slice(0, 3).map(p => (
        <div key={p.key} style={{ padding:12, borderRadius:8, background:'#fff', border:`1px solid ${p.isRecommended?G.green:G.cardBorder}` }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
            <strong style={{ fontSize:13, color:'#000' }}>{p.label}</strong>
            {p.isRecommended && <span style={{ fontSize:10, fontWeight:700, color:G.green }}>★ BEST FIT</span>}
          </div>
          <div style={{ fontSize:11, color:'#666', marginBottom:8 }}>From {p.typicalApr} APR · {p.typicalApprovalSeconds}s approval</div>
          {p.payments?.slice(0,3).map(pay => (
            <div key={pay.termMonths} style={{ display:'flex', justifyContent:'space-between', fontSize:12, padding:'2px 0', color:'#000' }}>
              <span style={{ color:'#666' }}>{pay.termMonths} mo</span>
              <strong>${pay.monthlyPaymentAllIn.toFixed(0)}/mo</strong>
            </div>
          ))}
        </div>
      ))}
    </div>

    <Btn v="primary" full onClick={apply} disabled={applying}>
      {applying ? <Spinner/> : `Apply for Financing · $${total.toFixed(0)}`}
    </Btn>
    <div style={{ fontSize:10, color:G.textMuted, textAlign:'center', marginTop:6 }}>
      Soft credit pull only. Subject to approval. Terms vary by partner. No prepayment penalty.
    </div>
  </div>;
}

// Scrollable page wrapper: alignItems:flex-start (NOT center) so the card
// scrolls naturally when its content exceeds the viewport. With center
// alignment the top of the card gets clipped off-screen on long estimates.
const page = { minHeight:'100vh', display:'flex', alignItems:'flex-start', justifyContent:'center', padding:'24px 16px 48px', background:G.bg, overflowY:'auto' };

// Shows the customer the payment schedule the owner/tech configured. Renders
// for deposit_balance and milestones (in_full is the default; no banner needed).
function PaymentPlanBanner({ plan, total }) {
  if (!plan) return null;
  if (plan.type === 'deposit_balance' && plan.deposit) {
    const pct = plan.deposit.kind === 'pct' ? Number(plan.deposit.value)||0 : (total>0 ? (Number(plan.deposit.value)||0)/total*100 : 0);
    const dep = +(total * (pct/100)).toFixed(2);
    const bal = Math.max(0, +(total - dep).toFixed(2));
    return <div style={{ marginTop:22, padding:18, borderRadius:12, border:`1px solid ${G.purple}66`, background: `linear-gradient(135deg, ${G.purpleDim}33, transparent)` }}>
      <h3 style={{ margin:'0 0 10px', fontSize:16 }}>💳 Payment Schedule</h3>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
        <div style={{ padding:12, borderRadius:8, background:G.input }}>
          <div style={{ fontSize:11, fontWeight:800, color:G.purple, textTransform:'uppercase' }}>Today (deposit)</div>
          <div style={{ fontSize:24, fontWeight:800, marginTop:4 }}>${dep.toFixed(0)}</div>
          <div style={{ fontSize:12, color:G.textMuted, marginTop:2 }}>{Math.round(pct)}% of ${total.toFixed(0)}</div>
        </div>
        <div style={{ padding:12, borderRadius:8, background:G.input }}>
          <div style={{ fontSize:11, fontWeight:800, color:G.textMuted, textTransform:'uppercase' }}>On completion</div>
          <div style={{ fontSize:24, fontWeight:800, marginTop:4 }}>${bal.toFixed(0)}</div>
          <div style={{ fontSize:12, color:G.textMuted, marginTop:2 }}>Balance after work is done</div>
        </div>
      </div>
    </div>;
  }
  if (plan.type === 'milestones' && Array.isArray(plan.milestones)) {
    return <div style={{ marginTop:22, padding:18, borderRadius:12, border:`1px solid ${G.purple}66`, background:`linear-gradient(135deg, ${G.purpleDim}33, transparent)` }}>
      <h3 style={{ margin:'0 0 10px', fontSize:16 }}>💳 Payment Milestones</h3>
      {plan.milestones.map((m, i) => {
        const amt = +(total * (Number(m.pct)||0)/100).toFixed(2);
        const trig = ({signed:'When you sign', on_site:'When tech arrives', midway:'Midway through', completed:'On completion', manual:'When invoiced'})[m.trigger || 'signed'];
        return <div key={i} style={{ display:'grid', gridTemplateColumns:'30px 1fr 110px 90px', gap:10, alignItems:'center', padding:'8px 0', borderTop: i===0 ? 'none' : `1px solid ${G.border}` }}>
          <div style={{ fontSize:16, fontWeight:800, color:G.purple, textAlign:'center' }}>{i+1}</div>
          <div>
            <div style={{ fontSize:14, fontWeight:600 }}>{m.label}</div>
            <div style={{ fontSize:11, color:G.textMuted }}>{trig}</div>
          </div>
          <div style={{ fontSize:13, color:G.textMuted, textAlign:'right' }}>{Math.round(Number(m.pct)||0)}%</div>
          <div style={{ fontSize:18, fontWeight:800, color:G.green, textAlign:'right' }}>${amt.toFixed(0)}</div>
        </div>;
      })}
      <div style={{ fontSize:11, color:G.textMuted, marginTop:10 }}>Each milestone is invoiced separately when its trigger fires. Pay each one via Apple Pay, Google Pay, Klarna, Affirm, card, or ACH.</div>
    </div>;
  }
  return null;
}
